import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Label, Select, Alert, TextInput } from 'flowbite-react';
import { staffApi, type StaffAssignmentCreateData } from '../../api/staff';
import { descriptorsApi, type Descriptor } from '../../api/descriptors';
import { dateInputBounds, validateDateFields } from '../../utils/dateValidation';

interface AddBoardMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffId: number;
  organizationId: number;
  organizationName: string;
  onSuccess: () => void;
}

/**
 * Specialized modal for adding board member assignments.
 *
 * Key behaviors:
 * 1. Shows dropdown of board roles (descriptors starting with "Board" or "School Board")
 * 2. Selected role's shortDescription becomes the positionTitle
 * 3. If user selects a specific board role (e.g., BoardPresident),
 *    we auto-create a BoardMember SEOAA as well (both with includeInDirectory=true)
 * 4. If user selects generic BoardMember, only one SEOAA is created
 */
export const AddBoardMemberModal: React.FC<AddBoardMemberModalProps> = ({
  isOpen,
  onClose,
  staffId,
  organizationId,
  organizationName,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roleDescriptors, setRoleDescriptors] = useState<Descriptor[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('');
  const [beginDate, setBeginDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [boardMemberDescriptorId, setBoardMemberDescriptorId] = useState<number | null>(null);

  // Load role descriptors on mount
  useEffect(() => {
    const loadDescriptors = async () => {
      try {
        const roles = await descriptorsApi.getByTypeName('Staff Role');
        setRoleDescriptors(roles);

        // Find the generic BoardMember descriptor for auto-tagging
        const boardMemberDesc = roles.find(
          r => r.codeValue === 'BoardMember' || r.codeValue === 'SchoolBoardMember'
        );
        if (boardMemberDesc) {
          setBoardMemberDescriptorId(boardMemberDesc.descriptorId);
        }
      } catch (err) {
        console.error('Failed to load role descriptors:', err);
      }
    };

    loadDescriptors();
  }, []);

  // Filter and sort board roles for the dropdown
  const boardRoles = useMemo(() => {
    return roleDescriptors
      .filter(r => {
        // Include if codeValue starts with "Board" OR shortDescription includes "School Board"
        const codeMatch = r.codeValue?.toLowerCase().startsWith('board');
        const descMatch = r.shortDescription?.toLowerCase().includes('school board');
        return codeMatch || descMatch;
      })
      .sort((a, b) => {
        // Put generic "Board Member" at the top, then alphabetically
        const aIsGeneric = a.codeValue === 'BoardMember' || a.codeValue === 'SchoolBoardMember';
        const bIsGeneric = b.codeValue === 'BoardMember' || b.codeValue === 'SchoolBoardMember';

        if (aIsGeneric && !bIsGeneric) return -1;
        if (!aIsGeneric && bIsGeneric) return 1;

        return (a.shortDescription || '').localeCompare(b.shortDescription || '');
      });
  }, [roleDescriptors]);

  // Get the selected descriptor
  const selectedDescriptor = useMemo(() => {
    if (!selectedRoleId) return null;
    return boardRoles.find(r => r.descriptorId === selectedRoleId);
  }, [selectedRoleId, boardRoles]);

  // Check if selected role is the generic BoardMember
  const isGenericBoardMember = useMemo(() => {
    if (!selectedDescriptor) return false;
    return selectedDescriptor.codeValue === 'BoardMember' ||
           selectedDescriptor.codeValue === 'SchoolBoardMember';
  }, [selectedDescriptor]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedRoleId('');
      setBeginDate('');
      setEndDate('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRoleId || !selectedDescriptor) {
      setError('Please select a board position');
      return;
    }

    const dateError = validateDateFields([
      { value: beginDate, label: 'Term Start Date' },
      { value: endDate, label: 'Term End Date' },
    ]);
    if (dateError) {
      setError(dateError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build the primary assignment data
      // The positionTitle comes from the descriptor's shortDescription
      const primaryAssignment: StaffAssignmentCreateData = {
        educationOrganizationId: organizationId,
        positionTitle: selectedDescriptor.shortDescription || undefined,
        roleDescriptorId: selectedDescriptor.descriptorId,
        beginDate: beginDate || undefined,
        endDate: endDate || undefined,
        isPrimary: true,
        includeInDirectory: true
      };

      // Create the primary assignment
      await staffApi.addAssignment(staffId, primaryAssignment);

      // If the selected role is NOT the generic BoardMember,
      // also create a BoardMember SEOAA for voting purposes
      if (!isGenericBoardMember && boardMemberDescriptorId) {
        const boardMemberTag: StaffAssignmentCreateData = {
          educationOrganizationId: organizationId,
          roleDescriptorId: boardMemberDescriptorId,
          beginDate: beginDate || undefined,
          endDate: endDate || undefined,
          isPrimary: false,
          includeInDirectory: true // Important: must be true for voting logic
        };

        await staffApi.addAssignment(staffId, boardMemberTag);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add board member assignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} dismissible popup size="lg">
      <div className="p-6">
        <h3 className="text-xl font-medium text-gray-900 mb-4">
          Add Board Member Assignment
        </h3>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Organization (locked) */}
          <div>
            <Label className="mb-2">Organization</Label>
            <div className="p-2.5 bg-gray-100 border border-gray-300 rounded-lg">
              <p className="text-gray-700 font-medium">{organizationName}</p>
            </div>
          </div>

          {/* Board Position Dropdown */}
          <div>
            <Label htmlFor="boardRole" className="mb-2">
              Board Position <span className="text-red-500">*</span>
            </Label>
            <Select
              id="boardRole"
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value ? Number(e.target.value) : '')}
              required
            >
              <option value="">Select a board position...</option>
              {boardRoles.map(role => (
                <option key={role.descriptorId} value={role.descriptorId}>
                  {role.shortDescription}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-sm text-gray-500">
              {isGenericBoardMember
                ? 'Generic board member - will be included in voting members list'
                : selectedDescriptor
                  ? 'A generic Board Member record will also be created for voting purposes'
                  : 'Select the board position for this member'
              }
            </p>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="beginDate" className="mb-2">
                Term Start Date
              </Label>
              <TextInput
                id="beginDate"
                type="date"
                value={beginDate}
                onChange={(e) => setBeginDate(e.target.value)}
                {...dateInputBounds}
              />
            </div>
            <div>
              <Label htmlFor="endDate" className="mb-2">
                Term End Date
              </Label>
              <TextInput
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                {...dateInputBounds}
              />
            </div>
          </div>

          {/* Info about what will be created */}
          {selectedDescriptor && (
            <Alert color="info" className="text-sm">
              <strong>What will be created:</strong>
              <ul className="mt-2 ml-4 list-disc text-xs">
                <li>
                  Primary assignment with position title: "{selectedDescriptor.shortDescription}"
                </li>
                {!isGenericBoardMember && boardMemberDescriptorId && (
                  <li>
                    Board Member tag (for voting member identification)
                  </li>
                )}
              </ul>
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button color="gray" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !selectedRoleId}>
              {loading ? 'Adding...' : 'Add Board Member'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
